import "./BusinessCard.scss";
import { useTranslation } from "react-i18next";
import { IconCheck } from "../../../../../../common/icons/IconCheck/IconCheck";
import { IconInfoCircle } from "../../../../../../common/icons/IconInfoCircle/IconInfoCircle";
import { getCountryFlag } from "../../../../../../common/util/getCountryFlag";
import LeafRectangleLoader from "../../../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import defaultContent from "../../../../../../content/profileSelectorDropdown";
import type { Business } from "../../../../../../services/MyBizShell/mybizShellAPI.types";

export interface BusinessCardProps {
  business: Business;
  isSelected?: boolean;
  isKpiLoading?: boolean;
  onSelect?: () => void;
}

export const BusinessCard = ({ isSelected = false, isKpiLoading = false, onSelect, business }: BusinessCardProps) => {
  const { t } = useTranslation(["profileSelectorDropdown"]);
  const dash = "\u2014";
  const masked = business.disabledAff;

  // Backend-resolved displayName is already translated via mapBusinessSelectorData → getDisplayName().
  // Only override for disabled affiliates (show dash).
  const displayName = masked ? dash : business.aboName;
  const displayGpv = masked ? dash : business.groupPv;
  const displayQualLegs = masked ? dash : business.qualifiedLegs;
  const displayBonus = masked ? dash : business.bonusPercent;

  return (
    <button
      type="button"
      className={`BusinessCard${isSelected ? " BusinessCard--selected" : ""}${business.privacyFlag && business.disabledAff ? " BusinessCard--disabled" : ""}${business.disabledAff ? " BusinessCard--confidential" : ""}`}
      onClick={onSelect}
      aria-pressed={isSelected}
      disabled={business.privacyFlag && business.disabledAff}
    >
      <div className="BusinessCard__header">
        <div className="BusinessCard__identity">
          <div className="BusinessCard__country-row">
            <span className="BusinessCard__flag" aria-hidden="true">
              {getCountryFlag(business.isoCountry)}
            </span>
            <span className="BusinessCard__abo-number">
              {business.isoCountry} {business.abo}
            </span>
            {business.privacyFlag && business.disabledAff && (
              <span
                className="BusinessCard__confidential-icon"
                aria-label={t("confidentialTooltip", defaultContent.confidentialTooltip)}
              >
                <IconInfoCircle />
                <div className="BusinessCard__confidential-tooltip" role="tooltip">
                  {t("confidentialTooltip", defaultContent.confidentialTooltip)}
                </div>
              </span>
            )}
          </div>
          <span className="BusinessCard__name">{displayName}</span>
        </div>
        {isSelected && (
          <span className="BusinessCard__check" aria-hidden="true">
            <IconCheck color="blue" />
          </span>
        )}
      </div>

      <div className="BusinessCard__metrics">
        <div className="BusinessCard__metric">
          <span className="BusinessCard__metric-label">{t("gpv", defaultContent.gpv)}</span>
          <LeafRectangleLoader isLoading={isKpiLoading} numRows={1} width="3rem" height="1.5rem" margin="0">
            <span className="BusinessCard__metric-value">{displayGpv}</span>
          </LeafRectangleLoader>
        </div>
        <div className="BusinessCard__metric">
          <span className="BusinessCard__metric-label">{t("qualifiedLegs", defaultContent.qualifiedLegs)}</span>
          <LeafRectangleLoader isLoading={isKpiLoading} numRows={1} width="3rem" height="1.5rem" margin="0">
            <span className="BusinessCard__metric-value">{displayQualLegs}</span>
          </LeafRectangleLoader>
        </div>
        <div className="BusinessCard__metric">
          <span className="BusinessCard__metric-label">{t("bonus", defaultContent.bonus)}</span>
          <LeafRectangleLoader isLoading={isKpiLoading} numRows={1} width="3rem" height="1.5rem" margin="0">
            <span className="BusinessCard__metric-value">{displayBonus}</span>
          </LeafRectangleLoader>
        </div>
      </div>
    </button>
  );
};
